import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { TOURS } from "@/data/content";
import { SectionHead } from "./Reveal";
import { useBooking } from "./BookingContext";

const SPANS = [
  "md:col-span-7 md:row-span-2",
  "md:col-span-5",
  "md:col-span-5",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
];

const spot = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
};

const TourCard = ({ tour, i }) => {
  const { t, lang } = useLang();
  const { choose } = useBooking();
  return (
    <motion.article
      initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      onMouseMove={spot}
      className={`spot-card group relative h-[420px] md:h-auto overflow-hidden rounded-[28px] border border-white/10 bg-[#16161C] ${SPANS[i]}`}
      data-testid={`tour-card-${tour.id}`}>
      <img src={tour.img} alt={tour.title} loading="lazy"
        className="absolute inset-0 h-full w-full object-cover brightness-[0.62] saturate-[0.85] transition-[transform,filter] duration-[1400ms] ease-out group-hover:scale-[1.06] group-hover:brightness-[0.8]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/30 to-transparent" />
      <div className="relative h-full flex flex-col justify-between p-6 md:p-8">
        <div className="flex items-start justify-between">
          <span className="font-display text-lg text-[#E5C158]">{String(i + 1).padStart(2, "0")}</span>
          <span className="flex items-center gap-2 rounded-full border border-white/15 bg-black/40 backdrop-blur-md px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-zinc-200">
            <Clock size={12} />{t.duration[tour.dur]}
          </span>
        </div>
        <div>
          <p className="eyebrow mb-3">{tour.region[lang]}</p>
          <h3 className={`font-display font-light text-[#F9F9F9] leading-none ${i === 0 ? "text-5xl md:text-7xl" : "text-4xl md:text-5xl"}`}>{tour.title}</h3>
          <p className="mt-4 max-w-sm text-sm md:text-base text-zinc-300">{tour.desc[lang]}</p>
          <div className="mt-6 flex items-center justify-between gap-4">
            {tour.price ? (
              <span className="text-zinc-300 text-sm" data-testid={`tour-price-${tour.id}`}>
                <span className="text-xs uppercase tracking-[0.2em] text-zinc-400 mr-2">{t.tours.from}</span>
                <span className="font-display text-3xl text-[#E5C158]">{tour.price.toLocaleString("pt-PT")} €</span>
              </span>
            ) : (
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t.tours.request}</span>
            )}
            <button onClick={() => choose(tour.id)} data-testid={`tour-book-${tour.id}`}
              className="flex items-center gap-2 rounded-full bg-[#F9F9F9] text-black px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold transition-[background-color,transform] duration-300 hover:bg-[#E5C158] active:scale-95">
              {t.tours.book}<ArrowUpRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export const Tours = () => {
  const { t } = useLang();
  return (
    <section id="tours" className="relative py-28 md:py-40 bg-[#0A0A0C]" data-testid="tours-section">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <SectionHead over={t.tours.over} lines={t.tours.title} sub={t.tours.sub} testid="tours-heading" />
        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 md:auto-rows-[340px] gap-5 md:gap-6">
          {TOURS.map((tour, i) => <TourCard key={tour.id} tour={tour} i={i} />)}
        </div>
      </div>
    </section>
  );
};
